import React, { FC, useCallback, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames/bind';
import Video from '../../molecules/Video';
import Image from '../../atoms/ImageView';
import properties from '../../../modules/ajio-util/properties.js';
import { getCurrentStore } from '../../selectors/metaData';
import { getProductName, getProductCode, getProductDetails } from '../../selectors/productpage'
import { getPDPVideosFeatureConfig } from '../../selectors/featureFlagsSelector';
import screen from '../../constants/pageType.constant';
import { gtmEventPush, segmentEventPush } from '../../../modules/ga/GTMdatalayer';
import { replaceWithJioCDN } from '../../../../src/utils/imageHandling';
import styles from './FallbackableImage.pcss';

const cx = classNames.bind(styles);

interface FallbackableImageProps {
  src: string;
  id?: string | number;
  alt?: string;
  testId?: string;
  coverContainer?: boolean;
  imageCls?: string;
  customErroredImgCls?: string;
  htmlLoading?: 'lazy' | 'auto' | 'eager';
  fetchPriority?: 'high' | 'low' | 'auto';
  decoding?: 'sync' | 'async' | 'auto';
  handleClick?: (id: string | number) => void;
  placeHolder?: string;
  isVideo?: boolean;
  updateVideoDetails?: any;
  pdpVideosFeatureConfig?: any;
  thumbnailImage?: string;
  storeName?: string;
  productName?: string;
  productCode?: string;
  maximizedOpen?:boolean;
  videoSeen?: boolean;
  LCP_image?: HTMLImageElement | null;
  roleForImage?:string;
  ariaHidden?:boolean;
  isPlpOnLandingWidget?:boolean;
  opensDialog?:boolean;
  productDetails?: any;
}

const FallbackableImage: FC<FallbackableImageProps> = (props: FallbackableImageProps) => {
  const {
    id,
    src,
    alt,
    testId = '',
    htmlLoading,
    fetchPriority,
    decoding,
    handleClick,
    coverContainer,
    imageCls = '',
    customErroredImgCls = '',
    placeHolder,
    isVideo = false,
    updateVideoDetails,
    pdpVideosFeatureConfig,
    thumbnailImage = '',
    storeName = 'ajio',
    productName,
    productCode,
    maximizedOpen=false,
    videoSeen = false,
    LCP_image,
    roleForImage = "",
    ariaHidden = false,
    isPlpOnLandingWidget = false,
    opensDialog = false,
    productDetails
  } = props;
  const { pdp: { pageType = '', pageName = '' } = {} } = screen;
  useEffect(() => {
    if(enableVideosOnPDP === 'ON' && isVideo && videoSeen) {
      gtmEventPush({
        page_name: pageName,
        page_type: pageType,
        event: 'pdp_catalog_video_interactions',
        event_category: 'pdp_catalog_video_interactions',
        event_action: 'video impression',
        product_id: productCode,
        product_name: productName
      });
      segmentEventPush({
        page_name: pageName,
        page_type: pageType,
        event: 'Pdp Product Image Details Interaction',
        event_category: 'pdp_catalog_video_interactions',
        event_action: 'video impression',
        product: [{
          product_brand: productDetails?.brandName,
          product_brick: productDetails?.brickName,
          product_id: productDetails?.code || productDetails?.baseProduct,
          product_list_price: productDetails?.price?.discountValue,
          product_mrp: productDetails?.price?.value,
          product_name: productDetails?.name,
          product_rating: productDetails?.aggregateRating?.averageRating,
        }],
        video_seen: true
      });
    }
  },[videoSeen]);

  const { enableVideosOnPDP = 'OFF', autoPlay = 'ON' } = pdpVideosFeatureConfig;
  const isPDPVideosFeatureEnabled = enableVideosOnPDP === 'ON';
  const isAutoPlayEnabled = autoPlay === 'ON';

  const [loadSuccess, setLoadSuccess] = useState<boolean>(false);
  const [hasErrored, setHasErrored] = useState<boolean>(false);

  const handleError = useCallback(() => {
    setLoadSuccess(false);
    setHasErrored(true);
  }, []);
  const handleLoaded = useCallback(() => {
    setLoadSuccess(true);
    setHasErrored(false);
  }, []);

  const minimizeVideoClick =() => {
    if(isVideo && enableVideosOnPDP === 'ON') {
      gtmEventPush({
        page_name: pageName,
        page_type: pageType,
        event: 'pdp_catalog_video_interactions',
        event_category: 'pdp_catalog_video_interactions',
        event_action:  !maximizedOpen ? 'full screen' : 'minimized',
        event_label: !maximizedOpen ? 'expand full screen' : 'minimize full screen',
        product_id: productCode,
        product_name: productName
      });
      handleSegmentEventPush();
      if (handleClick) handleClick(id, true);
    }
  };

  const handleSegmentEventPush = () => {
    segmentEventPush({
        page_name: pageName,
        page_type: pageType,
        event: 'Pdp Product Image Details Interaction',
        event_category: 'pdp_catalog_video_interactions',
        product: [{
          product_brand: productDetails?.brandName,
          product_brick: productDetails?.brickName,
          product_id: productDetails?.code || productDetails?.baseProduct,
          product_list_price: productDetails?.price?.discountValue,
          product_mrp: productDetails?.price?.value,
          product_name: productDetails?.name,
          product_rating: productDetails?.aggregateRating?.averageRating,
        }],
        video_screen: !maximizedOpen ? 'fullscreen' : 'minimized',
        video_impression: true,
      });
  }

  const onClick = useCallback(() => {
      if (handleClick) handleClick(id);
  }, [handleClick, id]);


  const fallbackImgCls = cx({
    'hide-image': loadSuccess,
    'cover-container': coverContainer,
    [imageCls]: Boolean(imageCls),
    [customErroredImgCls]: Boolean(customErroredImgCls) && (
      isPlpOnLandingWidget ? loadSuccess : !loadSuccess
    )
  });
  const imgCls = cx({
    [isPlpOnLandingWidget ? 'hide-image' : 'img-loading']: !loadSuccess && !hasErrored,
    'cover-container': coverContainer,
    [imageCls]: Boolean(imageCls) && loadSuccess,
    [customErroredImgCls]: Boolean(customErroredImgCls) && hasErrored
  });

  return (
    <>
      <Image
        key={`placehlder_${id}`}
        src={placeHolder}
        alt={`placehlder for ${alt}`}
        loading={htmlLoading || 'auto'}
        className={fallbackImgCls}
        ariaHidden={true}
      />
      {isPDPVideosFeatureEnabled ? (
        !isVideo ? (
          <Image
            key={id}
            src={replaceWithJioCDN(src)}
            alt={alt}
            testId={testId}
            loading={htmlLoading}
            fetchPriority={fetchPriority}
            decoding={decoding}
            handleClick={onClick}
            handleOnLoad={handleLoaded}
            handleError={handleError}
            className={imgCls}
            roleForImage={roleForImage}
            ariaHidden={ariaHidden}
            opensDialog={opensDialog}
          />
        ) : (
          <div style={{ minWidth: storeName === 'luxe' ? '100%' : '100vw', height : storeName === 'luxe' ? '52vh' : 'auto' }}>
            <Video
              videoURL={src}
              autoPlay={isAutoPlayEnabled}
              muted={true}
              loop={isAutoPlayEnabled}
              displayProgressBar={false}
              showPlayPauseHandler={true}
              displayBottomGradient={false}
              onClickOpenModal={true}
              showTimestamp={true}
              isModal={false}
              rowNumber={1}
              isShopThelook={false}
              isLuxe={storeName === 'luxe'}
              videoIsOnBanner={true}
              triggerVideoViewedGA={false}
              deviceType={'msite'}
              displayTopGradient={false}
              currentIndex={0}
              scrollBarExits={true}
              updateVideoDetails={updateVideoDetails}
              isFloatingWidget={false}
              thumbnailImage={thumbnailImage}
              onClickHandler={onClick}
              borderRadius={0}
              isFleek={storeName === 'ajiogram'}
              muteIconRight={false}
              pdpVideos={true}
              minimizeVideoClick={minimizeVideoClick}
              productCode={productCode}
              productName={productName}
              maximizedOpen={maximizedOpen}
              productDetails={productDetails}
            />
          </div>
        )
      ) : (
        <Image
          LCP_image={LCP_image}
          key={id}
          src={replaceWithJioCDN(src)}
          alt={alt}
          testId={testId}
          loading={htmlLoading}
          fetchPriority={fetchPriority}
          decoding={decoding}
          handleClick={onClick}
          handleOnLoad={handleLoaded}
          handleError={handleError}
          className={imgCls}
          roleForImage={roleForImage}
          ariaHidden={ariaHidden}
          opensDialog={opensDialog}
        />
      )}
    </>
  );
};

FallbackableImage.defaultProps = {
  alt: '',
  htmlLoading: 'auto',
  handleClick: undefined,
  id: 'ajio_image',
  coverContainer: false,
  placeHolder: `${properties.getProperty('imageBaseUrl')}/static/img/placeHolder.svg`
};

const mapStateToProps = state => {

  const pdpVideosFeatureConfig = getPDPVideosFeatureConfig(state);
  const storeName = getCurrentStore(state);
  const productName = getProductName(state);
  const productCode = getProductCode(state);
  const productDetails = getProductDetails(state);


  return {
    pdpVideosFeatureConfig,
    storeName,
    productName,
    productCode,
    productDetails
  };
};

export default connect(mapStateToProps, null)(FallbackableImage);
